/* eslint-disable relay/must-colocate-fragment-spreads */
import React, { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  graphql,
  useFragment,
  useLazyLoadQuery,
  useRelayEnvironment,
} from 'react-relay';
import * as yup from 'yup';

import { useMeta, usePooling } from '@woovi/hooks';
import type { FetchKeyProp } from '@woovi/relay';
import { useMutationCallbacks, withRelayBoundary } from '@woovi/relay';
import { CHARGE_STATUS_ENUM, EventType } from '@woovi/shared';

import type { ChargeAddMutation } from './__generated__/ChargeAddMutation.graphql.ts';
import type { PixDialogGetCharge_charge$key } from './__generated__/PixDialogGetCharge_charge.graphql.ts';
import type { PixDialogGetChargeQuery } from './__generated__/PixDialogGetChargeQuery.graphql.ts';
import { ChargeAdd, updater } from './ChargeAddMutation.tsx';
import DialogPixLoading from './DialogPixLoading.tsx';
import MetaPixelScript from './MetaPixelScript.tsx';
import { notifyListeners } from './notifyListeners.tsx';
import PixErrorDialog from './PixError.tsx';
import Plugin from './Plugin.tsx';
import type { OpenPixStoreCustomer } from '../dataLayer/OpenPixStore.tsx';
import fetchChargeQuery from '../order/fetchChargeQuery.tsx';
import { PLUGIN_POLLING_INTERVAL } from '../poolingInterval.tsx';
import useStoreContext from '../store/useStoreContext.tsx';
import { useChargeUpdateStatusSubscription } from '../subscription/useChargeUpdatedStatus.ts';

type getCustomerInput = OpenPixStoreCustomer | null;

type PixDialogCustomer = {
  name: string;
  email: string;
  phone: string;
  taxID: string;
};

type Props = {
  onClose: () => void;
  onCloseSuccess: () => void;
} & FetchKeyProp;

const PixDialog = ({ onClose, onCloseSuccess, ...props }: Props): ReactNode => {
  const environment = useRelayEnvironment();
  const { openPixState, setOpenPixState } = useStoreContext();
  const [error, setError] = useState(null);
  const { t } = useTranslation();

  const { isOpen, status } = openPixState;

  const correlationID = openPixState.correlationID?.toString();

  const response = useLazyLoadQuery<PixDialogGetChargeQuery>(
    graphql`
      query PixDialogGetChargeQuery($correlationID: String) {
        ...Plugin_query
        charge(correlationID: $correlationID) {
          ...PixDialogGetCharge_charge
        }
      }
    `,
    {
      correlationID,
    },
    {
      fetchPolicy: 'store-or-network',
      fetchKey: props.fetchKey,
    },
  );

  const charge = useFragment<PixDialogGetCharge_charge$key>(
    graphql`
      fragment PixDialogGetCharge_charge on Charge {
        ...Plugin_charge
        ...Order_charge
        ...useMeta_charge
        status
        correlationID
        metaPixel {
          token
        }
      }
    `,
    response.charge,
  );

  const getShouldPool = (): boolean => {
    if (!charge) {
      return false;
    }

    const isChargeCompleted = charge?.status === CHARGE_STATUS_ENUM.COMPLETED;
    const isChargeExpired = charge?.status === CHARGE_STATUS_ENUM.EXPIRED;
    const isChargePaid = status === CHARGE_STATUS_ENUM.COMPLETED;

    if (isChargeCompleted) {
      return false;
    }

    if (isChargeExpired) {
      return false;
    }

    if (isChargePaid) {
      return false;
    }

    // improve should pooling to cobv

    return true;
  };

  const shouldPooling = getShouldPool();

  usePooling(
    shouldPooling,
    () => fetchChargeQuery(environment, correlationID, setOpenPixState),
    PLUGIN_POLLING_INTERVAL,
  );

  useChargeUpdateStatusSubscription({
    input: {
      chargeId: charge?.id,
    },
  });

  useEffect(() => {
    if (charge?.status === CHARGE_STATUS_ENUM.EXPIRED) {
      notifyListeners({
        type: EventType.CHARGE_EXPIRED,
        data: {},
      });
    }

    if (charge?.status === CHARGE_STATUS_ENUM.COMPLETED) {
      notifyListeners({
        type: EventType.CHARGE_COMPLETED,
        data: {},
      });

      setOpenPixState({ success: true });
      const data = {
        status: CHARGE_STATUS_ENUM.COMPLETED,
      };

      notifyListeners({
        type: EventType.PAYMENT_STATUS,
        data,
      });
    }
  }, [charge?.status]);

  const [chargeAddMutation] = useMutationCallbacks<ChargeAddMutation>({
    name: 'ChargeAdd',
    mutation: ChargeAdd,
    shouldShowSnackbar: false,
    afterCompleted: (response) => {
      if (response.ChargeAdd.error) {
        setError(response.ChargeAdd.error);
        notifyListeners({
          type: EventType.ON_ERROR,
          data: {
            error: response?.ChargeAdd?.error,
          },
        });
      }
    },
    afterError: (error) => {
      notifyListeners({
        type: EventType.ON_ERROR,
        data: {
          error,
        },
      });

      // graphql errors
      if (error?.errors) {
        // improve this error with proper frontend input value validations
        setError(t('Error generating charge, check your input'));

        return;
      }

      if (error) {
        setError(error?.toString());
      }
    },
  });

  // generate a new charge when correlationID changes
  useEffect(() => {
    const createCharge = () => {
      if (!correlationID) {
        return;
      }

      if (charge?.correlationID === correlationID?.toString()) {
        // do not try to create a new charge if the current one is the same

        return;
      }

      const {
        type,
        customerRegister,
        value,
        description,
        customer,
        additionalInfo,
        expiresIn,
        enableCashbackPercentage = true,
        enableCashbackExclusivePercentage = true,
        splits,
        availablePaymentMethods,
      } = openPixState;

      const getCustomer = (
        customer: getCustomerInput,
      ): PixDialogCustomer | object => {
        if (!customer || !customer.name) {
          return {};
        }

        return {
          customer: {
            name: customer.name,
            email: customer.email,
            phone: customer.phone,
            taxIDValue: customer.taxID,
          },
        };
      };

      const getSplits = (): unknown => {
        if (splits) {
          return {
            splits,
          };
        }

        return {};
      };

      const getAvailablePaymentMethods = (): {
        availablePaymentMethods?: string[];
      } => {
        if (!availablePaymentMethods) {
          return {};
        }

        return { availablePaymentMethods };
      };

      const input = {
        type,
        value,
        correlationID: correlationID?.toString(),
        comment: description,
        additionalInfo,
        expiresIn,
        enableCashbackPercentage,
        enableCashbackExclusivePercentage,
        customerRegister,
        ...getCustomer(customer),
        ...getSplits(),
        ...getAvailablePaymentMethods(),
      };

      try {
        const schemaPixPay = yup.object().shape({
          value: yup
            .number()
            .typeError(t('Value should be a number'))
            .required(t('Value is required')),
          correlationID: yup
            .string()
            .typeError(t('CorrelationID should be a string'))
            .required(t('CorrelationID is required')),
          description: yup
            .string()
            .typeError(t('Description should be a string')),
          customer: yup.object().typeError(t('Customer should be an object')),
          closeOnSuccess: yup
            .boolean()
            .typeError(t('closeOnSuccess should be a boolean')),
          additionalInfo: yup
            .array()
            .of(
              yup.object().shape({
                key: yup
                  .string()
                  .typeError(t('Additional info key should be a string'))
                  .required(t('Additional info key is required')),
                value: yup
                  .string()
                  .typeError(t('Additional info value should be a string'))
                  .required(t('Additional info value is required')),
              }),
            )
            .typeError(
              t(
                'AdditionalInfo should be an array of objects containing key and value',
              ),
            ),
          identifier: yup
            .string()
            .typeError(t('Identifier should be a string')),
          expiresIn: yup.number().typeError(t('ExpiresIn should be a number')),
          enableCashbackPercentage: yup
            .boolean()
            .typeError(t('enableCashbackPercentage should be a boolean')),
          enableCashbackExclusivePercentage: yup
            .boolean()
            .typeError(
              t('enableCashbackExclusivePercentage should be a boolean'),
            ),
          modal: yup.boolean().typeError(t('Modal should be a boolean')),
          availablePaymentMethods: yup
            .array()
            .of(
              yup
                .string()
                .typeError(t('Each payment method should be a string')),
            ),
          type: yup.string().typeError(t('type should be a string')),
        });

        const inputValidated = schemaPixPay.validateSync(input, {
          strict: true,
        });

        const chargeAddConfig = {
          variables: {
            input: inputValidated,
          },
          updater: updater(correlationID),
        };

        // clean up error
        setError(null);
        chargeAddMutation(chargeAddConfig);
      } catch (err) {
        // eslint-disable-next-line
        console.error(err);
        setError(err.message);

        return;
      }
    };

    createCharge();
  }, [correlationID]);

  // discuss
  // if (!correlationID) {
  //   return null;
  // }

  useMeta({ charge });

  if (error) {
    return <PixErrorDialog isOpen={isOpen} error={error} onClose={onClose} />;
  }

  if (!charge) {
    return <DialogPixLoading isOpen={isOpen} />;
  }

  const getMetaPixelScript = (): React.ReactNode => {
    const token = charge?.metaPixel?.token;

    if (!token) {
      return null;
    }

    return <MetaPixelScript token={token} charge={charge} />;
  };

  return (
    <>
      <Plugin
        query={response}
        charge={charge}
        onCloseSuccess={onCloseSuccess}
        onClose={onClose}
        setError={setError}
      />
      {getMetaPixelScript()}
    </>
  );
};

export default withRelayBoundary(PixDialog);
